import React, { useEffect, useState } from 'react';
import { RefreshCw, FileText, AlertCircle, Download, Trash2, Plus } from 'lucide-react';
import MobileFAB from '../MobileFAB';
import useMobileHeaderActions from '../../hooks/useMobileHeaderActions';
import useTechnicianFilter from '../../hooks/useTechnicianFilter';
import TechnicianFilter from '../TechnicianFilter';
import authService from '../../services/authService';
import invoiceService from '../../services/invoiceService';
import jobService from '../../services/jobService';
import customerService from '../../services/customerService';
import { getFranchiseeId } from './services/invoiceHelpers';

// Import hooks
import { useNotifications } from './hooks/useNotifications';
import { useInvoiceState } from './hooks/useInvoiceState';
import { useInvoiceData } from './hooks/useInvoiceData';
import { useInvoiceActions } from './hooks/useInvoiceActions';

// Import components
import NotificationBanner from './components/NotificationBanner';
import ImportButton from './components/ImportButton';
import InvoiceFilters from './components/InvoiceFilters';
import InvoiceTable from './components/InvoiceTable';
import InvoicePagination from './components/InvoicePagination';
import InvoiceModal from './components/InvoiceModal';
import InvoiceForm from './components/InvoiceForm';

const InvoiceList = ({ userProfile, preselectedCustomer, previousView, onClearPreselectedCustomer, onGoToPreviousView, onCustomerSelect, onJobSelect, onAddJob, onAddContract, onAddJobForCustomer }) => {
  // Initialize hooks
  const notifications = useNotifications();
  const invoiceState = useInvoiceState();
  const techFilter = useTechnicianFilter(userProfile);
  const { fetchInvoices } = useInvoiceData(invoiceState, userProfile, techFilter);
  const invoiceActions = useInvoiceActions(invoiceState, userProfile, notifications, fetchInvoices, onCustomerSelect);

  // Extract commonly used state
  const {
    invoices,
    loading,
    error,
    searchTerm,
    currentPage,
    totalPages,
    totalInvoices,
    filters,
    showFilters,
    importing,
    importProgress,
    selectedInvoice,
    showInvoiceForm,
    editingInvoice,
    isQbConnected,
    hasNewRecords,
    isOnFirstPage
  } = invoiceState;

  // Auto-open invoice form when preselected customer is provided
  useEffect(() => {
    if (preselectedCustomer && !showInvoiceForm) {
      invoiceState.setShowInvoiceForm(true);
    }
  }, [preselectedCustomer, showInvoiceForm, invoiceState]);

  // Navigate to job details from invoice row
  // Track job_ids that returned 404 so we can hide the link
  const [missingJobIds, setMissingJobIds] = useState(new Set());
  const handleViewJob = async (invoice) => {
    if (!invoice.job_id || !onJobSelect) return;
    try {
      const franchiseeId = invoice.franchisee_id || (userProfile?.role === 'admin' ? 'all' : userProfile?.id);
      const job = await jobService.getJob(franchiseeId, invoice.job_id);
      if (job) onJobSelect(job);
      else setMissingJobIds(prev => new Set(prev).add(invoice.job_id));
    } catch (error) {
      setMissingJobIds(prev => new Set(prev).add(invoice.job_id));
    }
  };

  // Add job for a specific invoice's customer — fetch full customer record for address data
  const handleAddJobFromInvoice = async (invoice) => {
    if (!onAddJobForCustomer || !invoice.customer_id) return;
    try {
      const franchiseeId = invoice.franchisee_id || (userProfile?.role === 'admin' ? 'all' : userProfile?.id);
      const result = await customerService.getCustomer(franchiseeId, invoice.customer_id);
      const customer = result?.customer || result;
      if (customer && customer.id) {
        onAddJobForCustomer(customer);
      }
    } catch (error) {
      console.error('Error loading customer for job:', error);
      // Fallback to basic invoice data
      onAddJobForCustomer({
        id: invoice.customer_id,
        name: invoice.customer_name,
        email: invoice.customer_email,
        phone: invoice.customer_phone,
        company_name: invoice.customer_company,
        franchisee_id: invoice.franchisee_id
      });
    }
  };

  useMobileHeaderActions(
    <>
      {techFilter.showFilter && <TechnicianFilter {...techFilter} compact />}
      {isQbConnected && (userProfile?.role === 'admin' || authService.hasAdminReturn()) && (
        <button
          onClick={invoiceActions.handleImportFromQuickBooks}
          disabled={importing || invoiceActions.isSSEImporting}
          className="btn btn-sm btn-secondary"
        >
          <img src="/quickbooks-logo.svg" alt="QB" className="w-4 h-4" />
          {(importing || invoiceActions.isSSEImporting) ? (
            <RefreshCw size={14} className="animate-spin" />
          ) : (
            <Download size={14} />
          )}
          <span>{(importing || invoiceActions.isSSEImporting) ? 'Importing...' : 'Import'}</span>
        </button>
      )}
    </>,
    [isQbConnected, importing, invoiceActions.isSSEImporting, userProfile?.role, techFilter.showFilter, techFilter.selectedTechnician, techFilter.techGroups]
  );

  // Invoice form handlers
  const handleInvoiceFormSubmit = async (invoiceData) => {
    const franchiseeId = getFranchiseeId(userProfile);
    if (!franchiseeId) {
      notifications.showNotification('No franchisee ID found', 'error');
      return;
    }

    try {
      if (editingInvoice) {
        await invoiceService.updateInvoice(franchiseeId, editingInvoice.id, invoiceData);
      } else {
        // TODO: Implement create API call
        console.log('Creating new invoice:', invoiceData);
      }

      // Close form and refresh data
      invoiceState.setShowInvoiceForm(false);
      invoiceState.setEditingInvoice(null);
      await fetchInvoices();

      notifications.showNotification(
        editingInvoice ? 'Invoice updated successfully' : 'Invoice created successfully',
        'success'
      );
    } catch (error) {
      console.error('Error submitting invoice:', error);
      notifications.showNotification(
        `Failed to ${editingInvoice ? 'update' : 'create'} invoice: ${error.message}`,
        'error'
      );
    }
  };

  const handleEditInvoice = async (invoice) => {
    try {
      const franchiseeId = getFranchiseeId(userProfile);
      // Fetch full invoice with line items
      const fullInvoice = await invoiceService.getInvoice(franchiseeId, invoice.id);
      invoiceState.setEditingInvoice(fullInvoice);
      invoiceState.setShowInvoiceForm(true);
    } catch (error) {
      console.error('Error loading invoice for editing:', error);
      notifications.showNotification(`Failed to load invoice: ${error.message}`, 'error');
    }
  };

  const handleCloseInvoiceForm = () => {
    invoiceState.setShowInvoiceForm(false);
    invoiceState.setEditingInvoice(null);
    // Clear preselected customer to prevent form from reopening
    if (onClearPreselectedCustomer) {
      onClearPreselectedCustomer();
    }
    // If we came from another page (like customer details) and have a preselected customer,
    // navigate back to that page
    if (preselectedCustomer && previousView && onGoToPreviousView) {
      onGoToPreviousView();
    }
  };

  // Early error return
  if (error && !invoices.length) {
    return (
      <div className="px-2 py-2 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 py-2 md:p-6">
      <div className="mb-6">
        <MobileFAB onAddJob={onAddJob} onAddContract={onAddContract} />
        {/* Desktop Layout */}
        <div className="hidden md:flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
            <p className="text-gray-600">
              {totalInvoices} invoices found
              {userProfile?.role === 'franchisee' && ` for ${userProfile.name}`}
            </p>
          </div>

          <div className="flex items-center space-x-3">
            {onAddJob && (
              <button onClick={onAddJob} className="btn btn-md btn-secondary">
                <Plus size={16} />
                <span>Add Job</span>
              </button>
            )}
            {onAddContract && (
              <button onClick={onAddContract} className="btn btn-md btn-secondary">
                <Plus size={16} />
                <span>Add Contract</span>
              </button>
            )}
            <ImportButton
              isQbConnected={isQbConnected}
              userProfile={userProfile}
              importing={importing}
              isSSEImporting={invoiceActions.isSSEImporting}
              sseProgress={invoiceActions.sseProgress}
              importProgress={importProgress}
              totalInvoices={totalInvoices}
              onImport={invoiceActions.handleImportFromQuickBooks}
              onDeleteAll={invoiceActions.handleDeleteAllInvoices}
            />
            <TechnicianFilter {...techFilter} />
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden mb-2">
          <p className="text-gray-600 text-xs">
            {totalInvoices} invoices found
            {userProfile?.role === 'franchisee' && ` for ${userProfile.name}`}
          </p>
          {authService.hasAdminReturn() && userProfile?.role !== 'admin' && totalInvoices > 0 && (
            <button
              onClick={invoiceActions.handleDeleteAllInvoices}
              className="btn btn-sm btn-danger w-full mt-2"
              title="Admin only: Delete all invoices (for testing)"
            >
              <Trash2 size={14} />
              <span className="text-xs font-bold">Delete All ({totalInvoices})</span>
            </button>
          )}
        </div>

        <NotificationBanner
          hasNewRecords={hasNewRecords}
          isOnFirstPage={isOnFirstPage}
          onGoToFirstPage={() => {
            invoiceState.setCurrentPage(1);
            invoiceState.setHasNewRecords(false);
          }}
          onDismiss={() => invoiceState.setHasNewRecords(false)}
        />

        <InvoiceFilters
          searchTerm={searchTerm}
          onSearch={invoiceActions.handleSearch}
          filters={filters}
          onFilterChange={invoiceActions.handleFilterChange}
          showFilters={showFilters}
          onToggleFilters={() => invoiceState.setShowFilters(!showFilters)}
          onClearFilters={invoiceActions.clearFilters}
        />
      </div>

      {/* Invoice List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="animate-spin text-gray-400" size={24} />
          <span className="ml-2 text-gray-600">Loading invoices...</span>
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || Object.values(filters).some(f => f)
              ? 'Try adjusting your search or filters'
              : 'Import invoices from QuickBooks to get started'
            }
          </p>
        </div>
      ) : (
        <>
          <InvoiceTable
            invoices={invoices}
            onViewInvoice={invoiceActions.handleViewInvoice}
            onViewPDF={invoiceActions.handleViewPDF}
            onDeleteInvoice={invoiceActions.handleDeleteInvoice}
            onEditInvoice={handleEditInvoice}
            onViewCustomer={invoiceActions.handleViewCustomer}
            onViewJob={handleViewJob}
            missingJobIds={missingJobIds}
            onAddJobFromInvoice={handleAddJobFromInvoice}
            userProfile={userProfile}
          />

          <InvoicePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalInvoices={totalInvoices}
            onPageChange={invoiceState.setCurrentPage}
          />
        </>
      )}

      {/* Invoice Detail Modal */}
      <InvoiceModal
        invoice={selectedInvoice}
        onClose={() => invoiceState.setSelectedInvoice(null)}
        onEdit={(inv) => { invoiceState.setSelectedInvoice(null); handleEditInvoice(inv); }}
      />

      {/* Invoice Form Modal (edit mode or preselected customer) */}
      {showInvoiceForm && (
        <InvoiceForm
          invoice={editingInvoice}
          isEditing={!!editingInvoice}
          preselectedCustomer={preselectedCustomer}
          showCustomerInTitle={!!preselectedCustomer}
          onClose={handleCloseInvoiceForm}
          onSubmit={handleInvoiceFormSubmit}
          user={userProfile}
        />
      )}

      {/* Error Toast (for remaining error states) */}
      {error && (
        <div className="fixed bottom-6 right-6 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center space-x-2">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;
